import { AddIcon } from "@chakra-ui/icons";
import PageLayout from "../../../components/page-layout";
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Flex,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Spinner,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { SportType } from "../../../models/court/dto/court.dto";
import { paramFromWindowLocation } from "../../../utils/url-parser";
import { getMoovingPoint, isMoovingArenaEnabledForOrg } from "../../../utils/levels";
import { GenericGeneralInformation } from "./GenericGeneralInformation";
import UserCouponList from "../../coupon-management/components/UserCouponList";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";
import UserWalletSection from "../../wallet-admin/components/UserWalletSection";
import UserHistorySection from "../../user-history/components/UserHistorySection";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import SelectCohortModal from "../../user-cohort/components/SelectCohortModal";
import {
    GET_USER_COHORTS_QUERY_KEY,
    useGetUserCohorts,
} from "../../user-cohort/hooks/useGetUserCohorts";
import {
    useRemoveUserFromCohortMutation,
} from "../../user-cohort/hooks/useRemoveUserFromCohort";
import { useArchiveUser, useUnarchiveUser } from "../hooks/useArchiveUser";
import ChangeUserPasswordModal from "./ChangeUserPasswordModal";
import { useCreatePaymentLink } from "../hooks/useCreatePaymentLink";
import { useGetPayLaterBookings } from "../hooks/useGetPayLaterBookings";
import { useGetPaymentLinks } from "../hooks/useGetPaymentLinks";
import { useGetSignupDocumentUrl } from "../hooks/useGetSignupDocumentUrl";
import { SignupDocumentVerificationPanel } from "./SignupDocumentVerificationPanel";
import { useGetUser } from "../hooks/useGetUser";
import { useNotifyUser } from "../hooks/useNotifyUser";
import { useUpdateUserProfile } from "../hooks/useUpdateUserProfile";
import { parsePrice } from "../../../utils/parse-price";
import { useValidateSportLevel } from "../hooks/useValidateSportLevel";
import { useValidateSportLevelIdentification } from "../hooks/useValidateSportLevelIdentification";
import DuoAttachmentCard from "./DuoAttachmentCard";
import UserMembershipList from "./UserMembershipList";
import UserAdditionalFieldsCard from "./UserAdditionalFieldsCard";
import AccessLogsTable from "../../access-control/components/AccessLogsTable";
import ErrorState from "../../../components/states/ErrorState";
import InlineErrorState from "../../../components/states/InlineErrorState";
import ScopePrompt from "../../_shared/ScopePrompt";
import {
    classifyRead,
    READ_FAILED_I18N_KEY,
    SELECT_ORGANIZATION_I18N_KEY,
} from "../../_shared/readState";


export default function UserPage() {
    const userId = paramFromWindowLocation('user');
    const toast = useToast();
    const hoverBg = useColorModeValue('gray.50', 'gray.700');
    const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
    const textColorStrong = useColorModeValue('gray.800', 'gray.200');

    // The whole page is this one read, and until run 112 it took no error flag
    // and used no loading flag: `isLoading` was destructured and never
    // referenced, so a refused `GET users/:id/details` — and the first paint of
    // every visit — rendered a COMPLETE, fully-interactive profile of a user we
    // had not read. Two of the fields it invented are permission claims, and
    // both fail OPEN:
    //
    //   "Can book courts"  `data?.canBookCourts !== false` → "Yes", with a
    //                      CHECKED and enabled checkbox beside it. A user the
    //                      API would have reported as `false` reads as allowed,
    //                      and one click writes `canBookCourts: false` over a
    //                      value nobody ever read back.
    //   "Status"           `data?.isArchived ? … : …` → "Active", next to an
    //                      "Archive user" button, for a user whose archived
    //                      state is unknown.
    //
    // `classifyRead` rather than a private truthiness test (D59), so a
    // BACKGROUND refetch that fails over a profile already on screen keeps that
    // profile — blanking a correct page because a refresh failed is the mirror
    // of the defect (R96-3).
    const { data, isError, isInitialLoading, refetch } = useGetUser(userId);
    const userReadState = classifyRead(isError, isInitialLoading, data);

    const { mutateAsync: updateUserProfile, isLoading: isUpdatingProfile } = useUpdateUserProfile(userId, () => {
        toast({
            title: translate('user.profile_updated_successfully'),
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    });
    const { mutateAsync: validateSportLevel, isLoading: isValidatingLevel } = useValidateSportLevel(userId, (data) => {
        toast({
            title: translate('user.sport_level_validation_updated') || 'Sport level validation updated',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    });
    const { mutateAsync: validateSportLevelIdentification, isLoading: isValidatingIdentification } = useValidateSportLevelIdentification(userId, (data) => {
        toast({
            title: translate('user.sport_level_validation_updated') || 'Sport level validation updated',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    });
    const { mutateAsync: archiveUser, isLoading: isArchiving } = useArchiveUser(userId);
    const { mutateAsync: unarchiveUser, isLoading: isUnarchiving } = useUnarchiveUser(userId);
    const { mutateAsync: notifyUser, isLoading: isNotifying } = useNotifyUser(userId);
    const { data: paymentLinks, isLoading: paymentLinksLoading } = useGetPaymentLinks(userId);
    const { mutateAsync: createPaymentLink, isLoading: isCreatingPaymentLink } = useCreatePaymentLink(userId);
    const { data: payLaterBookings, isLoading: payLaterBookingsLoading } = useGetPayLaterBookings(userId);
    // API exposes `hasSignupDocument` as a boolean instead of the raw blob
    // path (which is PII-adjacent). The full SAS URL comes from the admin-only
    // useGetSignupDocumentUrl below.
    const hasSignupDocument = Boolean(data?.hasSignupDocument);
    // The error flag is not optional here, and the card below says why: its
    // only non-preview branch is "Loading document…". Until run 125 that text
    // was also what a REFUSED read rendered — the signup-document route is
    // admin-only, so a 403 (or any failure) left an administrator staring at a
    // load that had already finished and would never be retried, beside a
    // verification panel still offering Approve and Reject. `isInitialLoading`
    // is false for a disabled query, so it is the flag that separates "in
    // flight" from "never asked" without this file re-deriving the hook's own
    // `enabled` expression (D62).
    const {
        data: signupDocData,
        isError: isSignupDocError,
        isInitialLoading: isSignupDocLoading,
    } = useGetSignupDocumentUrl(userId, hasSignupDocument);
    const signupDocState = classifyRead(isSignupDocError, isSignupDocLoading, signupDocData);
    const { isOpen: isNotifyModalOpen, onOpen: onNotifyModalOpen, onClose: onNotifyModalClose } = useDisclosure();
    const { isOpen: isCreatePaymentLinkOpen, onOpen: onCreatePaymentLinkOpen, onClose: onCreatePaymentLinkClose } = useDisclosure();
    const { isOpen: isChangePasswordOpen, onOpen: onChangePasswordOpen, onClose: onChangePasswordClose } = useDisclosure();
    const [notificationTitle, setNotificationTitle] = useState("");
    const [notificationBody, setNotificationBody] = useState("");
    const [notificationUrl, setNotificationUrl] = useState("");
    const [paymentLinkAmount, setPaymentLinkAmount] = useState("");
    const [paymentLinkCurrency, setPaymentLinkCurrency] = useState<"eur" | "usd">("eur");
    const [paymentLinkDescription, setPaymentLinkDescription] = useState("");
    const [createdPaymentLinkUrl, setCreatedPaymentLinkUrl] = useState<string | null>(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");

    // Sync inline fields when user data loads
    useEffect(() => {
        if (data) {
            setFirstName(data.firstName ?? "");
            setLastName(data.lastName ?? "");
            setPhoneNumber(data.contactInfo?.phoneNumber ?? "");
        }
    }, [data?.firstName, data?.lastName, data?.contactInfo?.phoneNumber]);

    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const queryClient = useQueryClient();
    // The cohorts card is a SECOND read on this page, and run 112's fix did not
    // reach it. `useGetUserCohorts` is gated `enabled: Boolean(userId &&
    // organizationId)` while the page itself is gated on `userId` alone
    // (`useGetUser`), so the two gates differ by exactly one term — and that term
    // is the one a whole session can be missing. An operator with no
    // organization selected reaches this page normally (by link, by search, from
    // a game) and the profile renders, because `GET users/:id/details` needs no
    // organization; the cohorts query beside it is never issued, React Query v4
    // reports the disabled query as `isLoading: true`, and the card below read
    // that raw flag. The result was a card stuck on "Loading..." for the rest of
    // the session, next to an "Add to cohort" button that still opens.
    //
    // The refused case is the same card's other half and it is the worse
    // sentence: `!userCohorts?.length` is true for `undefined`, so a
    // `GET cohorts?organizationId=…&userId=…` the API declined to answer
    // rendered "Not in any cohorts" — a definitive claim about a member's
    // memberships, made from a question that got no answer.
    //
    // `classifyRead` rather than a private truthiness test (D59), so the same
    // four states this campaign has settled on elsewhere apply here, and a
    // BACKGROUND refetch that fails over cohorts already on screen keeps them
    // (R96-3).
    const {
        data: userCohorts,
        isError: isCohortsError,
        isInitialLoading: isCohortsInitialLoading,
    } = useGetUserCohorts(userId, selectedOrganization?.id);
    const cohortsReadState = classifyRead(
        isCohortsError,
        isCohortsInitialLoading,
        userCohorts,
    );
    const removeFromCohort = useRemoveUserFromCohortMutation();
    const navigate = useNavigate();
    const {
        isOpen: isAddToCohortOpen,
        onOpen: onAddToCohortOpen,
        onClose: onAddToCohortClose,
    } = useDisclosure();

    // Get list of sports the user has levels for
    const userSports = useMemo(() => {
        return data?.sportLevels?.map(sl => sl.sportType) || [];
    }, [data?.sportLevels]);

    // Get list of sports available at the selected location
    const locationSports = useMemo(() => {
        return selectedLocation?.sports || [];
    }, [selectedLocation?.sports]);

    // Filter to only show sports that both the user has AND the location supports
    const availableSports = useMemo(() => {
        if (!selectedLocation || !locationSports.length) {
            // If no location selected or location has no sports, show all user sports
            return userSports;
        }
        // Return intersection: sports the user has that are also available at the location
        return userSports.filter(sport => locationSports.includes(sport));
    }, [userSports, locationSports, selectedLocation]);

    // Initialize with 'main', will be updated to first available sport when data loads
    const [selectedSport, setSelectedSport] = useState<SportType | 'main'>('main');
    const [updatedLevel, setUpdatedLevel] = useState(() => {
        // Initialize with the level for the selected sport
        if (selectedSport === 'main') {
            return data?.level?.toString();
        }
        const sportLevel = data?.sportLevels?.find(sl => sl.sportType === selectedSport);
        return sportLevel?.level?.toString() || data?.level?.toString();
    });

    // Update level when sport selection changes
    useEffect(() => {
        if (data) {
            if (selectedSport === 'main') {
                setUpdatedLevel(data.level?.toString());
            } else {
                const sportLevel = data.sportLevels?.find(sl => sl.sportType === selectedSport);
                setUpdatedLevel(sportLevel?.level?.toString() || data.level?.toString());
            }
        }
    }, [selectedSport, data]);

    const isUserLevelValid = useMemo(() => {
        const isValidNumber = !isNaN(Number.parseFloat(updatedLevel)) && updatedLevel !== '' && updatedLevel !== undefined;
        const isClamped = Number.parseFloat(updatedLevel) >= 1 && Number.parseFloat(updatedLevel) <= 10;
        return isValidNumber && isClamped;
    }, [updatedLevel])

    // Get current level for display
    const currentLevel = useMemo(() => {
        if (selectedSport === 'main') {
            return data?.level;
        }
        const sportLevel = data?.sportLevels?.find(sl => sl.sportType === selectedSport);
        return sportLevel?.level ?? data?.level;
    }, [selectedSport, data]);

    // Get current identification number for the selected sport
    const currentIdentificationNumber = useMemo(() => {
        if (selectedSport === 'main') {
            return undefined;
        }
        const sportLevel = data?.sportLevels?.find(sl => sl.sportType === selectedSport);
        return sportLevel?.identificationNumber;
    }, [selectedSport, data]);

    // Get current official ranking for the selected sport
    const currentOfficialRanking = useMemo(() => {
        if (selectedSport === 'main') {
            return undefined;
        }
        const sportLevel = data?.sportLevels?.find(sl => sl.sportType === selectedSport);
        return sportLevel?.officialRanking;
    }, [selectedSport, data]);

    // Mooving Arena point for the selected sport (Padel only today, MYS-39).
    // Previews the in-progress edited level when valid, else the saved level.
    const moovingArenaPoint = useMemo(() => {
        if (selectedSport === 'main') return undefined;
        if (!isMoovingArenaEnabledForOrg(selectedOrganization?.id)) return undefined;
        const effectiveLevel = isUserLevelValid ? Number.parseFloat(updatedLevel) : currentLevel;
        return getMoovingPoint(selectedSport, effectiveLevel);
    }, [selectedSport, isUserLevelValid, updatedLevel, currentLevel, selectedOrganization?.id]);

    // Level verification (admin verified the level). Support legacy isValidated.
    const isSportLevelVerified = useMemo(() => {
        if (selectedSport === 'main') return false;
        const sportLevel = data?.sportLevels?.find(sl => sl.sportType === selectedSport);
        return !!(sportLevel?.isLevelVerified);
    }, [selectedSport, data]);

    // Identification / license verification (admin verified identification number or official ranking)
    const isSportLevelIdentificationVerified = useMemo(() => {
        if (selectedSport === 'main') return false;
        const sportLevel = data?.sportLevels?.find(sl => sl.sportType === selectedSport);
        return sportLevel?.isIdentificationVerified || false;
    }, [selectedSport, data]);

    // Update selected sport when availableSports changes (e.g., when data loads or location changes)
    // Always select the first available sport (not "Main") when sports become available
    useEffect(() => {
        if (availableSports.length > 0) {
            // If current selection is 'main' or not in available sports, select first available sport
            if (selectedSport === 'main' || !availableSports.includes(selectedSport)) {
                setSelectedSport(availableSports[0]);
            }
        } else if (selectedSport !== 'main') {
            // If no available sports, fall back to 'main'
            setSelectedSport('main');
        }
    }, [availableSports, selectedSport]);
    const handleArchiveUser = async () => {
        try {
            await archiveUser();
            toast({
                title: translate('user.archived_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: translate('user.archive_failed'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleUnarchiveUser = async () => {
        try {
            await unarchiveUser();
            toast({
                title: translate('user.unarchived_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: translate('user.unarchive_failed'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleNotifyUser = async () => {
        try {
            await notifyUser({
                title: notificationTitle,
                body: notificationBody,
                url: notificationUrl || undefined,
            });
            toast({
                title: translate('user.notification_sent_successfully') || 'Notification sent successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onNotifyModalClose();
            setNotificationTitle("");
            setNotificationBody("");
            setNotificationUrl("");
        } catch (error) {
            toast({
                title: translate('user.notification_send_failed') || 'Failed to send notification',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const isNotificationFormValid = notificationTitle.trim() !== "" && notificationBody.trim() !== "";

    const handleCreatePaymentLink = async () => {
        const amount = parsePrice(paymentLinkAmount);
        if (Number.isNaN(amount) || amount <= 0) {
            toast({ title: translate("user.payment_link_invalid_amount_toast"), status: "error", duration: 3000, isClosable: true });
            return;
        }
        const amountCents = Math.round(amount * 100);
        try {
            const result = await createPaymentLink({ amountCents, currency: paymentLinkCurrency, description: paymentLinkDescription.trim() || undefined });
            setCreatedPaymentLinkUrl(result.url);
            toast({ title: translate("user.payment_link_created_toast"), status: "success", duration: 3000, isClosable: true });
        } catch {
            toast({ title: translate("user.payment_link_failed_toast"), status: "error", duration: 3000, isClosable: true });
        }
    };

    const closeCreatePaymentLinkModal = () => {
        setCreatedPaymentLinkUrl(null);
        setPaymentLinkAmount("");
        setPaymentLinkDescription("");
        onCreatePaymentLinkClose();
    };

    const profileChanged =
        (data?.firstName ?? "") !== firstName ||
        (data?.lastName ?? "") !== lastName ||
        (data?.contactInfo?.phoneNumber ?? "") !== phoneNumber;
    const levelChanged = currentLevel !== undefined && Number.parseFloat(updatedLevel) !== currentLevel;
    const hasValidLevelChange = levelChanged && isUserLevelValid;
    const hasChanges = profileChanged || hasValidLevelChange;
    const isSaving = isUpdatingProfile;

    const handleSaveDetails = async () => {
        try {
            const payload: Parameters<typeof updateUserProfile>[0] = {};
            if (profileChanged) {
                payload.firstName = firstName.trim() || undefined;
                payload.lastName = lastName.trim() || undefined;
                payload.phoneNumber = phoneNumber.trim() || undefined;
            }
            if (hasValidLevelChange) {
                payload.level = Number.parseFloat(updatedLevel);
                payload.sportType = selectedSport === 'main' ? undefined : selectedSport;
            }
            await updateUserProfile(payload);
        } catch {
            toast({
                title: translate('user.profile_update_failed') || 'Failed to update profile',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const inputField = <>
        <Flex gap={2} alignItems="center" mb={2}>
            <Select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value as SportType | 'main')}
                width="220px"
            >
                <option value="main">{translate('user.main_level')}</option>
                {availableSports.sort().map((sport) => (
                    <option key={sport} value={sport}>
                        {sport}
                    </option>
                ))}
            </Select>
            <Input type="number" value={updatedLevel} onChange={(newValue) => {
                setUpdatedLevel(newValue.target.value);
            }} />
        </Flex>
        {selectedSport !== 'main' && (
            <Flex direction="column" gap={2} mb={2} mt={1}>
                {moovingArenaPoint !== undefined && (
                    <Flex gap={2} alignItems="center">
                        <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">
                            {translate('user.mooving_arena_point')}:
                        </Text>
                        <Text fontSize="sm" color={textColorStrong} fontWeight="semibold">
                            {moovingArenaPoint}
                        </Text>
                    </Flex>
                )}
                <Checkbox
                    isChecked={isSportLevelVerified}
                    onChange={(e) => {
                        validateSportLevel({
                            sportType: selectedSport,
                            isLevelVerified: e.target.checked,
                        });
                    }}
                    isDisabled={isValidatingLevel}
                    colorScheme="brand"
                >
                    <Text fontSize="sm" fontWeight="medium">
                        {translate('user.level_verified_by_admin') || 'Level verified by Admin'}
                    </Text>
                </Checkbox>
                <Checkbox
                    isChecked={isSportLevelIdentificationVerified}
                    onChange={(e) => {
                        validateSportLevelIdentification({
                            sportType: selectedSport,
                            isIdentificationVerified: e.target.checked,
                        });
                    }}
                    isDisabled={isValidatingIdentification}
                    colorScheme="green"
                >
                    <Text fontSize="sm" fontWeight="medium">
                        {translate('user.identification_verified_by_admin') || 'Identification / License verified by Admin'}
                    </Text>
                </Checkbox>
                {currentIdentificationNumber && (
                    <Flex gap={2} alignItems="center">
                        <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">
                            {translate('user.identification_number')}:
                        </Text>
                        <Text fontSize="sm" color={textColorStrong}>
                            {currentIdentificationNumber}
                        </Text>
                    </Flex>
                )}
                {currentOfficialRanking && (
                    <Flex gap={2} alignItems="center">
                        <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">
                            {translate('user.official_ranking')}:
                        </Text>
                        <Text fontSize="sm" color={textColorStrong}>
                            {currentOfficialRanking}
                        </Text>
                    </Flex>
                )}
            </Flex>
        )}
        {<FormLabel color={'red.400'} h={4}>{isUserLevelValid ? '' : translate('user.user_level_invalid')}</FormLabel>}
    </>

    // Placed AFTER every hook in this component, so nothing here is a
    // conditional hook call — the two arms below are the last statements before
    // the page proper.
    //
    // The chrome is kept deliberately: `PageLayout` + a `PageCard` that still
    // carries the back button and the title, so an operator who lands here from
    // the roster is not stranded on a blank screen. What is NOT kept is
    // `rightHeader` — Save, Notify, Change password, Archive/Unarchive — because
    // every one of those writes to a user whose current state we do not have.
    // That is the whole point of the arm: this page's failure mode was not a
    // missing table, it was a live write surface over an invented profile.
    if (userReadState === 'loading') {
        return (
            <PageLayout>
                <PageCard showBackButton title={translate('user.details')}>
                    <Flex justifyContent="center" py={10}>
                        <Spinner size="lg" />
                    </Flex>
                </PageCard>
            </PageLayout>
        );
    }

    // `unreadable` (the read was refused) and `never-issued` (the URL carries no
    // `?user=`, so `enabled: Boolean(id)` parked the query) are one arm on
    // purpose. Both mean "there is no profile here", and R98-1 settled that an
    // entity-id gate's honest render is a not-found surface rather than a
    // "choose something first" prompt — there is nothing on this page for the
    // operator to choose. Retry is offered only when there is an id to retry
    // WITH, which is the same condition `CoachDetailsPage` uses.
    if (userReadState !== 'answered') {
        return (
            <PageLayout>
                <PageCard showBackButton title={translate('user.details')}>
                    <ErrorState
                        message={translate(READ_FAILED_I18N_KEY as any)}
                        onRetry={userId ? () => { refetch(); } : undefined}
                    />
                </PageCard>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <PageCard showBackButton title={translate('user.details')} rightHeader={
                <Flex gap={2}>
                    <Button
                        isDisabled={!hasChanges}
                        isLoading={isSaving}
                        colorScheme="brand"
                        onClick={handleSaveDetails}
                    >
                        {translate('submit')}
                    </Button>
                    <Button
                        colorScheme="purple"
                        variant="outline"
                        onClick={onNotifyModalOpen}
                    >
                        {translate('user.notify_user') || 'Notify User'}
                    </Button>
                    <Button
                        colorScheme="brand"
                        variant="outline"
                        onClick={onChangePasswordOpen}
                    >
                        {translate('user.change_password')}
                    </Button>
                    {data?.isArchived ? (
                        <Button
                            isLoading={isUnarchiving}
                            colorScheme="green"
                            onClick={handleUnarchiveUser}
                        >
                            {translate('user.unarchive_user')}
                        </Button>
                    ) : (
                        <Button
                            isLoading={isArchiving}
                            colorScheme="red"
                            variant="outline"
                            onClick={handleArchiveUser}
                        >
                            {translate('user.archive_user')}
                        </Button>
                    )}
                </Flex>
            }>
                <Box display="flex" flexDirection="column" p={4}>
                    <GenericGeneralInformation cardProps={{
                        gridArea: {
                            base: '2 / 1 / 3 / 2',
                            lg: '1 / 2 / 2 / 3'
                        },
                        minH: '365px',
                        pe: '20px'
                    }}
                        items={[
                            { title: translate('user.first_name'), content: data?.firstName, overrideContent: <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} size="sm" /> },
                            { title: translate('user.last_name'), content: data?.lastName, overrideContent: <Input value={lastName} onChange={(e) => setLastName(e.target.value)} size="sm" /> },
                            { title: translate('user.email'), content: data?.email, },
                            { title: translate('user.level'), content: data?.level.toString(), overrideContent: inputField },
                            { title: translate('user.phone_number'), content: data?.contactInfo?.phoneNumber || '-', overrideContent: <Input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} size="sm" placeholder={translate('user.phone_number')} /> },
                            { title: translate('user.postal_code'), content: data?.contactInfo?.postalCode || '-' },
                            {
                                title: translate('user.welcome_email'),
                                content: (() => {
                                    const status = data?.emailDeliveryStatus;
                                    const label = status === 'delivered' ? translate('user.email_delivered')
                                        : status === 'pending' ? translate('user.email_pending')
                                        : status === 'bounced' ? translate('user.email_bounced')
                                        : status === 'failed' ? translate('user.email_failed')
                                        : translate('user.email_not_sent');
                                    return label;
                                })(),
                                overrideContent: (() => {
                                    const status = data?.emailDeliveryStatus;
                                    const colorScheme = status === 'delivered' ? 'green'
                                        : status === 'pending' ? 'yellow'
                                        : status === 'bounced' || status === 'failed' ? 'red'
                                        : 'gray';
                                    const label = status === 'delivered' ? translate('user.email_delivered')
                                        : status === 'pending' ? translate('user.email_pending')
                                        : status === 'bounced' ? translate('user.email_bounced')
                                        : status === 'failed' ? translate('user.email_failed')
                                        : translate('user.email_not_sent');
                                    return <Badge colorScheme={colorScheme} fontSize="xs" px={2} py={1} borderRadius="md">{label}</Badge>;
                                })(),
                            },
                            { title: translate('user.status'), content: data?.isArchived ? translate('user.status_archived') : translate('user.status_active') },
                            {
                                title: translate('user.can_book_courts'),
                                content: data?.canBookCourts !== false ? translate('common.yes') : translate('common.no'),
                                overrideContent: (
                                    <Checkbox
                                        isChecked={data?.canBookCourts !== false}
                                        isDisabled={isUpdatingProfile}
                                        onChange={(e) => {
                                            updateUserProfile({ canBookCourts: e.target.checked });
                                        }}
                                        colorScheme="brand"
                                    >
                                        <Text fontSize="sm">{data?.canBookCourts !== false ? translate('common.yes') : translate('common.no')}</Text>
                                    </Checkbox>
                                ),
                            },
                            {
                                title: translate('user.can_use_pay_later'),
                                content: data?.canUsePayLater !== false ? translate('common.yes') : translate('common.no'),
                                overrideContent: (
                                    <Checkbox
                                        isChecked={data?.canUsePayLater !== false}
                                        isDisabled={isUpdatingProfile}
                                        onChange={(e) => {
                                            updateUserProfile({ canUsePayLater: e.target.checked });
                                        }}
                                        colorScheme="brand"
                                    >
                                        <Text fontSize="sm">{data?.canUsePayLater !== false ? translate('common.yes') : translate('common.no')}</Text>
                                    </Checkbox>
                                ),
                            },
                        ]}
                    />
                </Box>
            </PageCard>

            <UserWalletSection userId={userId} />

            {/* Unified Player History (epic: docs/epics/unified-player-history.md,
                D-PLACE) — directly below the Wallet section. WalletTransactionTable
                was removed from UserWalletSection above once T8@eng-review's
                superset test (UserHistorySection.test.tsx) proved an
                ADMIN_ADJUSTMENT row round-trips through this section — a
                strict subset, per D-PLACE. */}
            <UserHistorySection userId={userId} />

            <PageCard
                title={translate("cohort.cohorts")}
                rightHeader={
                    <Button
                        leftIcon={<AddIcon />}
                        size="sm"
                        colorScheme="brand"
                        onClick={onAddToCohortOpen}
                        // R117-2 — a WRITE affordance is a different contract
                        // from a read's render (R106-3), and run 116 left this
                        // one enabled while fixing the card below it. With no
                        // organization selected there is nothing for the dialog
                        // to offer (`useGetCohorts` is gated on exactly that
                        // term) and nothing for its success handler to
                        // invalidate — `GET_USER_COHORTS_QUERY_KEY(userId,
                        // selectedOrganization?.id ?? "")` keys on the empty
                        // string. So the write is impossible, not merely empty,
                        // and the honest control is a disabled one.
                        //
                        // Disabled WITH its reason: the title says why on
                        // hover, and the card body immediately below renders
                        // `ScopePrompt gate="organization"` in exactly this
                        // state. That pairing is the whole justification for
                        // disabling rather than prompting inside the dialog,
                        // so it is pinned as a PAIR rather than left to a
                        // reader — `_shared/user-page-cohorts-read-state.test.tsx`
                        // ("the 'Add to cohort' write affordance"), whose first
                        // test asserts the disabled button and the ScopePrompt
                        // together, and which also pins that a REFUSED cohorts
                        // read leaves this button live.
                        isDisabled={!selectedOrganization}
                        /*
                         * The RAW constant on purpose, not `scopePromptI18nKey`
                         * (run 135). Every other site in this dashboard that
                         * renders this sentence was moved onto that function so
                         * it can say "you appear to be offline" instead when
                         * that is the real reason a read produced nothing
                         * (R134-4). This one must not follow, because it is not
                         * about a read at all: it is keyed on
                         * `!selectedOrganization`, i.e. on the store, so the
                         * button really is disabled for want of a scope and the
                         * network has nothing to do with it. Routing it through
                         * the function would tell an offline operator who has
                         * chosen no organization that the button is disabled
                         * because they are offline — the same class of false
                         * sentence, pointing the other way.
                         */
                        title={
                            !selectedOrganization
                                ? translate(
                                      SELECT_ORGANIZATION_I18N_KEY as any,
                                  )
                                : undefined
                        }
                    >
                        {translate("user.add_to_cohort")}
                    </Button>
                }
            >
                <Box p={4} px={6}>
                    {cohortsReadState === "loading" ? (
                        <Text color="gray.500">
                            {translate("cohort.loading")}
                        </Text>
                    ) : cohortsReadState === "never-issued" ? (
                        // No organization selected, so the read was never sent.
                        // The control that fixes it is the picker in the navbar,
                        // not anything on this page — hence the prompt rather
                        // than an error or a retry.
                        <ScopePrompt gate="organization" />
                    ) : cohortsReadState === "unreadable" ? (
                        <InlineErrorState
                            message={translate("cohort.error_loading")}
                        />
                    ) : !userCohorts?.length ? (
                        <Text color="gray.500">
                            {translate("cohort.no_cohorts_for_user")}
                        </Text>
                    ) : (
                        <VStack align="stretch" spacing={2}>
                            {userCohorts.map((cohort) => (
                                <Flex
                                    key={cohort.id}
                                    justify="space-between"
                                    align="center"
                                    p={2}
                                    borderRadius="md"
                                    _hover={{ bg: hoverBg }}
                                    cursor="pointer"
                                    onClick={() =>
                                        navigate(
                                            `/admin/user-management/cohorts/${cohort.id}`,
                                        )
                                    }
                                >
                                    <Box flex={1} minW={0}>
                                        <Text fontWeight="medium">
                                            {cohort.name}
                                        </Text>
                                        {cohort.description && (
                                            <Text
                                                fontSize="sm"
                                                color="gray.600"
                                                noOfLines={1}
                                            >
                                                {cohort.description}
                                            </Text>
                                        )}
                                    </Box>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        colorScheme="red"
                                        flexShrink={0}
                                        isLoading={
                                            removeFromCohort.isLoading &&
                                            removeFromCohort.variables?.cohortId ===
                                                cohort.id
                                        }
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            if (
                                                window.confirm(
                                                    `${translate("cohort.confirm_remove_user")} ${data?.firstName ?? ""} ${data?.lastName ?? ""} from "${cohort.name}"?`,
                                                )
                                            ) {
                                                try {
                                                    await removeFromCohort.mutateAsync(
                                                        {
                                                            cohortId: cohort.id,
                                                            userId,
                                                        },
                                                    );
                                                    toast({
                                                        title: translate(
                                                            "cohort.user_removed_successfully",
                                                        ),
                                                        status: "success",
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                } catch {
                                                    toast({
                                                        title: translate(
                                                            "cohort.remove_user_failed",
                                                        ),
                                                        status: "error",
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                }
                                            }
                                        }}
                                    >
                                        {translate(
                                            "cohort.remove_from_cohort",
                                        )}
                                    </Button>
                                </Flex>
                            ))}
                        </VStack>
                    )}
                </Box>
            </PageCard>

            <SelectCohortModal
                userIds={[userId]}
                isOpen={isAddToCohortOpen}
                onClose={onAddToCohortClose}
                onSuccess={() =>
                    queryClient.invalidateQueries({
                        queryKey: GET_USER_COHORTS_QUERY_KEY(
                            userId,
                            selectedOrganization?.id ?? "",
                        ),
                    })
                }
            />

            <UserCouponList userId={userId} />

            <UserMembershipList userId={userId} />

            {/* Rollout item 9 (Enzo Card passage-event ingestion) — the
                HYROX/plateau cardio-musculation passage history a club
                operator asked to see on a player's fiche client. Renders its
                own empty state when the player has no Enzo bracelet or no
                recorded passages yet. */}
            <AccessLogsTable
                userId={userId}
                title={translate('access_control.player_passages_title')}
            />

            {/* MYS-117 Duo — payer-side attach management (renders only when
                the user holds a subscription on a Duo plan). */}
            <DuoAttachmentCard userId={userId} />

            <UserAdditionalFieldsCard userId={userId} />

            {hasSignupDocument && (
                <PageCard title={translate("user.signup_document")}>
                    <Box p={4}>
                        {signupDocState === 'unreadable' ? (
                            // Never the "Loading document…" line below: the
                            // read is over and it failed. The verification
                            // panel still renders — its status falls back to
                            // the user-details read, which succeeded — but the
                            // operator now knows the document itself could not
                            // be fetched rather than believing it is still on
                            // its way. `never-issued` cannot occur inside this
                            // card: it renders only when `hasSignupDocument`
                            // is true, which is the hook's own `enabled`.
                            <InlineErrorState message={translate(READ_FAILED_I18N_KEY as any)} />
                        ) : signupDocData?.url ? (
                            <Box>
                                {signupDocData?.contentType === 'application/pdf' ? (
                                    <Box
                                        as="iframe"
                                        src={signupDocData.url}
                                        width="100%"
                                        height="500px"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        mb={3}
                                    />
                                ) : (
                                    <Box
                                        as="img"
                                        src={signupDocData.url}
                                        alt="Signup document"
                                        maxH="400px"
                                        borderRadius="md"
                                        objectFit="contain"
                                        mb={3}
                                    />
                                )}
                                <Button
                                    size="sm"
                                    colorScheme="brand"
                                    onClick={() => window.open(signupDocData.url, '_blank')}
                                >
                                    {translate("user.open_document")}
                                </Button>
                            </Box>
                        ) : (
                            <Text color="gray.500">{translate("user.loading_document")}</Text>
                        )}
                        <SignupDocumentVerificationPanel
                            userId={userId}
                            firstName={data?.firstName ?? ''}
                            lastName={data?.lastName ?? ''}
                            // status comes from the admin signup-document
                            // endpoint (with verification details), not from
                            // /users/:id/details — that path no longer exposes
                            // PII fields. Fall back to user.signupDocumentStatus
                            // (4-value enum, no PII) for the moment before the
                            // signup-document fetch resolves.
                            status={signupDocData?.status ?? data?.signupDocumentStatus}
                            result={signupDocData?.verificationResult}
                            verifiedAt={signupDocData?.verifiedAt}
                        />
                    </Box>
                </PageCard>
            )}

            <PageCard
                title={translate("user.payment_links")}
                rightHeader={
                    <Button
                        leftIcon={<AddIcon />}
                        size="sm"
                        colorScheme="brand"
                        onClick={onCreatePaymentLinkOpen}
                    >
                        {translate("user.create_payment_link")}
                    </Button>
                }
            >
                <Box p={4} px={6}>
                    {paymentLinksLoading ? (
                        <Text color="gray.500">{translate("user.loading")}</Text>
                    ) : !paymentLinks?.length ? (
                        <Text color="gray.500">{translate("user.no_payment_links_yet")}</Text>
                    ) : (
                        <TableContainer>
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>{translate("user.payment_link_description")}</Th>
                                        <Th>{translate("user.payment_link_amount")}</Th>
                                        <Th>{translate("user.payment_link_status")}</Th>
                                        <Th>{translate("user.payment_link_created")}</Th>
                                        <Th>{translate("user.payment_link_expires")}</Th>
                                        <Th>{translate("common.actions")}</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {paymentLinks.map((link) => {
                                        const canCopy = link.status === 'pending' && link.checkoutUrl;
                                        const isExpired = link.expiresAt && new Date(link.expiresAt) < new Date();
                                        const statusKey = link.status === 'paid' ? 'user.payment_link_status_paid' : link.status === 'expired' ? 'user.payment_link_status_expired' : 'user.payment_link_status_pending';
                                        const statusColorScheme = link.status === 'paid' ? 'green' : link.status === 'expired' ? 'gray' : 'blue';
                                        return (
                                            <Tr key={link.id}>
                                                <Td>{link.description || "—"}</Td>
                                                <Td>{(link.amount / 100).toFixed(2)} {link.currency.toUpperCase()}</Td>
                                                <Td>
                                                    <Badge colorScheme={statusColorScheme} fontSize="xs" px={2} py={1} borderRadius="md">
                                                        {translate(statusKey)}
                                                    </Badge>
                                                </Td>
                                                <Td>{new Date(link.createdAt).toLocaleString()}</Td>
                                                <Td>{link.expiresAt ? new Date(link.expiresAt).toLocaleString() : "—"}</Td>
                                                <Td>
                                                    {canCopy && !isExpired ? (
                                                        <Button
                                                            size="sm"
                                                            colorScheme="brand"
                                                            variant="outline"
                                                            onClick={() => {
                                                                if (link.checkoutUrl) {
                                                                    void navigator.clipboard.writeText(link.checkoutUrl);
                                                                    toast({ title: translate("user.copied_to_clipboard_toast"), status: "success", duration: 2000, isClosable: true });
                                                                }
                                                            }}
                                                        >
                                                            {translate("user.copy")}
                                                        </Button>
                                                    ) : link.status === 'pending' && isExpired ? (
                                                        <Text fontSize="sm" color="gray.500">{translate("user.payment_link_status_expired")}</Text>
                                                    ) : null}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </PageCard>

            <PageCard title={translate("user.pay_later_bookings")}>
                <Box p={4} px={6}>
                    {payLaterBookingsLoading ? (
                        <Text color="gray.500">{translate("user.loading")}</Text>
                    ) : !payLaterBookings?.length ? (
                        <Text color="gray.500">{translate("user.no_pay_later_bookings")}</Text>
                    ) : (
                        <TableContainer>
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>{translate("user.court")}</Th>
                                        <Th>{translate("user.location")}</Th>
                                        <Th>{translate("user.start")}</Th>
                                        <Th>{translate("user.payment_link_amount")}</Th>
                                        <Th>{translate("user.payment_link_status")}</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {payLaterBookings.map((b) => (
                                        <Tr
                                            key={b.paymentIntentId}
                                            cursor={b.gameId != null ? "pointer" : undefined}
                                            _hover={b.gameId != null ? { bg: hoverBg } : undefined}
                                            onClick={() => {
                                                if (b.gameId != null) {
                                                    navigate(`/admin/gameDetails?game=${b.gameId}`);
                                                }
                                            }}
                                        >
                                            <Td>{b.courtName}</Td>
                                            <Td>{b.locationName}</Td>
                                            <Td>{new Date(b.startTime).toLocaleString()}</Td>
                                            <Td>{(b.amount / 100).toFixed(2)} {b.currency.toUpperCase()}</Td>
                                            <Td>{b.status === "succeeded" ? translate("user.paid") : translate("user.unpaid")}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </PageCard>

            {/* Create Payment Link Modal */}
            <Modal isOpen={isCreatePaymentLinkOpen} onClose={closeCreatePaymentLinkModal} isCentered size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{translate("user.create_payment_link_modal_title")}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            {createdPaymentLinkUrl ? (
                                <Box>
                                    <FormLabel>{translate("user.payment_link_copy_share")}</FormLabel>
                                    <Flex gap={2}>
                                        <Input value={createdPaymentLinkUrl} isReadOnly size="sm" fontFamily="mono" />
                                        <Button
                                            size="sm"
                                            colorScheme="brand"
                                            onClick={() => {
                                                void navigator.clipboard.writeText(createdPaymentLinkUrl);
                                                toast({ title: translate("user.copied_to_clipboard_toast"), status: "success", duration: 2000, isClosable: true });
                                            }}
                                        >
                                            {translate("user.copy")}
                                        </Button>
                                    </Flex>
                                </Box>
                            ) : (
                                <>
                                    <Box>
                                        <FormLabel>{translate("user.amount")}</FormLabel>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min={0}
                                            placeholder={translate("user.amount_placeholder")}
                                            value={paymentLinkAmount}
                                            onChange={(e) => setPaymentLinkAmount(e.target.value)}
                                        />
                                    </Box>
                                    <Box>
                                        <FormLabel>{translate("user.currency")}</FormLabel>
                                        <Select
                                            value={paymentLinkCurrency}
                                            onChange={(e) => setPaymentLinkCurrency(e.target.value as "eur" | "usd")}
                                        >
                                            <option value="eur">EUR</option>
                                            <option value="usd">USD</option>
                                        </Select>
                                    </Box>
                                    <Box>
                                        <FormLabel>{translate("user.description_optional")}</FormLabel>
                                        <Input
                                            placeholder={translate("user.description_placeholder")}
                                            value={paymentLinkDescription}
                                            onChange={(e) => setPaymentLinkDescription(e.target.value)}
                                        />
                                    </Box>
                                </>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={closeCreatePaymentLinkModal} mr={3}>
                            {createdPaymentLinkUrl ? translate("common.close") : translate("common.cancel")}
                        </Button>
                        {!createdPaymentLinkUrl && (
                            <Button
                                size="sm"
                                colorScheme="brand"
                                onClick={handleCreatePaymentLink}
                                isLoading={isCreatingPaymentLink}
                                isDisabled={!paymentLinkAmount.trim()}
                            >
                                {translate("user.create_link")}
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <ChangeUserPasswordModal
                userId={userId}
                userEmail={data?.email}
                isOpen={isChangePasswordOpen}
                onClose={onChangePasswordClose}
            />

            {/* Notify User Modal */}
            <Modal isOpen={isNotifyModalOpen} onClose={onNotifyModalClose} isCentered size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{translate('user.notify_user') || 'Notify User'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <FormLabel>{translate('user.notification_title') || 'Title'}</FormLabel>
                                <Input
                                    value={notificationTitle}
                                    onChange={(e) => setNotificationTitle(e.target.value)}
                                    placeholder={translate('user.notification_title_placeholder') || 'Enter notification title'}
                                />
                            </Box>
                            <Box>
                                <FormLabel>{translate('user.notification_body') || 'Message'}</FormLabel>
                                <Textarea
                                    value={notificationBody}
                                    onChange={(e) => setNotificationBody(e.target.value)}
                                    placeholder={translate('user.notification_body_placeholder') || 'Enter notification message'}
                                    rows={4}
                                />
                            </Box>
                            <Box>
                                <FormLabel>{translate('user.notification_url') || 'URL (Optional)'}</FormLabel>
                                <Input
                                    value={notificationUrl}
                                    onChange={(e) => setNotificationUrl(e.target.value)}
                                    placeholder={translate('user.notification_url_placeholder') || 'e.g., game/123 or event/456'}
                                />
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onNotifyModalClose} mr={3}>
                            {translate('common.cancel') || 'Cancel'}
                        </Button>
                        <Button
                            colorScheme="purple"
                            onClick={handleNotifyUser}
                            isLoading={isNotifying}
                            isDisabled={!isNotificationFormValid}
                        >
                            {translate('user.send_notification') || 'Send Notification'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </PageLayout>
    );
}
